import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import {
  resolveCompany, passwordLogin, completeMfa, refreshMe, forgotPassword, errorOf,
} from '@/lib/tenantAuth';
import { fetchDemoAccounts } from '@dreamtree-org/landing-kit/auth';
import { getApiRoot } from '@/lib/domainResolver';
import PhoneSignIn from './PhoneSignIn';
import AuthShell from './AuthShell';

/**
 * Company sign-in (screens A01–A03, A05–A07 + the two-step step of A12): company first, then
 * the company-local account — by password, or by phone + MPIN when the company allows it.
 * `/c/:slug/login` (or a remembered company) skips the first step. Keeps the company
 * visible throughout so nobody signs into the wrong one.
 */
const REMEMBER_KEY = 'app:last-company';
// A blank company field signs into the `default` company (the demo:seed company, slug
// landing-kit DEMO_COMPANY); a deployment without one answers "could not find that company".
const DEFAULT_COMPANY = 'default';

function rememberedSlug() {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
}

export default function CompanyLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = useParams();
  const [slug, setSlug] = useState(params.slug || rememberedSlug());
  const [company, setCompany] = useState(null);
  const [preauthId, setPreauthId] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [ticket, setTicket] = useState(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState('password'); // password | phone | forgot
  const [notice, setNotice] = useState('');

  const resolve = async (value) => {
    setErr('');
    setBusy(true);
    try {
      const res = await resolveCompany(value);
      setCompany({ ...res.company, branding: res.branding, methods: res.methods || { password: true }, firebase: res.firebase || null });
      setMethod(res.methods && res.methods.password === false && res.methods.mpin ? 'phone' : 'password');
      setPreauthId(res.preauthId);
      try { localStorage.setItem(REMEMBER_KEY, res.company.slug); } catch { /* not essential */ }
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (params.slug) resolve(params.slug);
  }, [params.slug]);

  const finish = async (result) => {
    const auth = await refreshMe();
    if (result.needsStrongFactor || (auth && auth.needsStrongFactor)) return nav('/account/security', { replace: true });
    return nav((loc.state && loc.state.from) || '/', { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const res = await passwordLogin(preauthId, identifier, password);
      setPassword('');
      if (res.mfaRequired) setTicket(res.ticket);
      else await finish(res);
    } catch (ex) {
      const { message, code: errCode } = errorOf(ex);
      setErr(message);
      if (errCode === 'PREAUTH_EXPIRED') expired();
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await finish(await completeMfa(preauthId, ticket, code));
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
      setCode('');
    }
  };

  const changeCompany = () => {
    setCompany(null);
    setPreauthId(null);
    setTicket(null);
    setErr('');
    setNotice('');
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const res = await forgotPassword(preauthId, identifier);
      setNotice(res.deliveryAvailable
        ? 'If that account exists and has an email address, we sent it a reset link. It works once, for 15 minutes.'
        : 'Email delivery isn\'t set up for this company — ask your administrator to reset your password.');
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
    }
  };

  const expired = () => { setCompany(null); setPreauthId(null); };
  const methods = (company && company.methods) || {};
  const both = methods.password !== false && methods.mpin;

  // Both steps share this component's state, so a chip fills slug, identifier and password at
  // once; on the company step the user still clicks Continue.
  const demoAccounts = ticket ? undefined : {
    load: () => fetchDemoAccounts(`${getApiRoot()}/api/auth/demo-accounts`),
    onSelect: (a) => {
      if (a.company) {
        setSlug(a.company.toLowerCase());
        if (company && company.slug !== a.company) changeCompany();
      }
      setIdentifier(a.identifier);
      setPassword(a.password);
      setMethod('password');
      setErr('');
      setNotice('');
    },
  };

  return (
    <AuthShell
      title={company ? (company.branding.name || company.displayName) : 'Sign in'}
      subtitle={company ? (
        <span className="flex items-center gap-2 min-w-0">
          {company.branding && company.branding.logo_url
            ? <img src={company.branding.logo_url} alt="" className="h-5 w-5 object-contain shrink-0" />
            : null}
          <span className="truncate">{company.slug}</span>
        </span>
      ) : 'Enter your company to continue.'}
      demoAccounts={demoAccounts}
    >
      <div className="space-y-4">
        {err && <Alert variant="error">{err}</Alert>}
        {notice && <Alert variant="info">{notice}</Alert>}

        {company && !ticket && both && method !== 'forgot' && (
          <div className="grid grid-cols-2 gap-1 rounded-md bg-secondary-100 dark:bg-secondary-900 p-1 text-sm" role="tablist">
            {[['password', 'Password'], ['phone', 'Phone & MPIN']].map(([key, label]) => (
              <button
                key={key} type="button" role="tab" aria-selected={method === key}
                className={`rounded px-2 py-1 ${method === key ? 'bg-white dark:bg-secondary-800 shadow-sm font-medium' : 'text-secondary-500'}`}
                onClick={() => { setMethod(key); setErr(''); setNotice(''); }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {!company && (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); resolve(slug.trim() || DEFAULT_COMPANY); }}>
            <Input label="Company" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="your-company" autoComplete="organization" />
            <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Finding…' : 'Continue'}</Button>
          </form>
        )}

        {company && !ticket && method === 'phone' && (
          <>
            <PhoneSignIn preauthId={preauthId} firebase={company.firebase} mpinLength={methods.mpinLength || 6} onSignedIn={finish} onExpired={expired} />
            <button type="button" className="w-full text-xs text-secondary-500 hover:underline" onClick={changeCompany}>Not {company.displayName}? Change company</button>
          </>
        )}

        {company && !ticket && method === 'forgot' && (
          <form className="space-y-3" onSubmit={submitForgot}>
            <p className="text-sm text-secondary-600">Enter your email or username and we&apos;ll send a reset link to the account&apos;s email address.</p>
            <Input label="Email or username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required />
            <Button type="submit" className="w-full" disabled={busy || !identifier.trim()}>{busy ? 'Sending…' : 'Send reset link'}</Button>
            <button type="button" className="w-full text-xs text-secondary-500 hover:underline" onClick={() => { setMethod('password'); setNotice(''); }}>Back to sign in</button>
          </form>
        )}

        {company && !ticket && method === 'password' && (
          <form className="space-y-3" onSubmit={submitPassword}>
            <Input label="Email or username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required />
            <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</Button>
            <div className="flex justify-between text-xs">
              <button type="button" className="text-secondary-500 hover:underline" onClick={() => { setMethod('forgot'); setErr(''); }}>Forgot password?</button>
              <button type="button" className="text-secondary-500 hover:underline" onClick={changeCompany}>Change company</button>
            </div>
          </form>
        )}

        {company && ticket && (
          <form className="space-y-3" onSubmit={submitCode}>
            <p className="text-sm text-secondary-600">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
            <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus required />
            <Button type="submit" className="w-full" disabled={busy || !code.trim()}>{busy ? 'Verifying…' : 'Verify'}</Button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
